// Página pública dos Termos de Uso
// Conteúdo voltado ao usuário final (sem notas internas de revisão jurídica).
import React from "react";
import LegalDocument from "components/LegalDocument";

const CONTENT = `
## 1. Quem somos e o que é o TiqChat

Bem-vindo(a) ao **TiqChat**!

O TiqChat é uma plataforma de **entretenimento** que utiliza **Inteligência Artificial (IA)** para recriar personagens inspirados em figuras bíblicas — como Salomão, Davi, Ester e Daniel — com os quais você pode conversar por mensagens de texto e áudio.

Estes Termos de Uso ("Termos") regem o uso do site **www.tiqchat.com** e de todos os serviços oferecidos pela plataforma (em conjunto, o "TiqChat" ou "Plataforma").

O TiqChat é operado por:

- **Razão social:** Stonel19 Soluções em IA Gen Ltda
- **Nome fantasia:** Stonel19
- **CNPJ:** 61.577.764/0001-39
- **Endereço:** Rua Profª. Cremilde de Oliveira, 328 — Jardim Atlântico, Florianópolis/SC — CEP 88.095-560
- **E-mail de contato:** faglimar@stonel19.com
- **WhatsApp:** (16) 98202-6554

Ao acessar ou usar o TiqChat, você concorda com estes Termos e com a nossa [Política de Privacidade](/politica-de-privacidade). Se você **não concorda** com qualquer parte destes Termos, por favor, não utilize a Plataforma.

## 2. Natureza do serviço: entretenimento com IA

É fundamental que você entenda o que o TiqChat **é** e o que **não é**:

### 2.1 O que o TiqChat é
- Uma experiência de **entretenimento** baseada em Inteligência Artificial.
- Os personagens são **recriações fictícias geradas por IA**, inspiradas em figuras bíblicas, criadas para conversas leves, curiosas e culturalmente relevantes.
- As respostas buscam apoiar-se na visão de mundo da Bíblia, trazendo passagens que ilustram o que é dito.

### 2.2 O que o TiqChat NÃO é
- **NÃO é** uma fonte oficial de doutrina religiosa, nem representa qualquer igreja, denominação ou autoridade religiosa.
- **NÃO substitui** a leitura da Bíblia, o aconselhamento pastoral ou a orientação de líderes religiosos.
- **NÃO substitui** aconselhamento profissional de qualquer natureza — médico, psicológico, jurídico, financeiro ou de outra área.
- **NÃO é** uma pessoa real. Você está conversando com um sistema automatizado de IA.

### 2.3 Sobre o conteúdo gerado por IA
As respostas dos personagens são geradas automaticamente por modelos de Inteligência Artificial e podem conter **imprecisões, erros, citações bíblicas incorretas ou conteúdo que não reflete fielmente os textos originais**. A Stonel19 não garante a exatidão, completude ou adequação das respostas para qualquer finalidade específica. O uso das informações é de responsabilidade exclusiva do usuário.

## 3. Idade mínima

O uso do TiqChat é destinado **exclusivamente a pessoas maiores de 18 (dezoito) anos**.

Ao se cadastrar, você declara e garante que tem **18 anos completos ou mais** e que possui plena capacidade civil para aceitar estes Termos. Caso identifiquemos que um usuário é menor de 18 anos, sua conta poderá ser suspensa ou encerrada.

## 4. Cadastro e conta de usuário

### 4.1 Criação de conta
Para utilizar o TiqChat, você precisa criar uma conta fornecendo um e-mail válido e uma senha. Você é responsável por:
- Fornecer informações **verdadeiras, exatas e atualizadas**;
- Manter a **confidencialidade da sua senha**;
- Todas as atividades realizadas em sua conta.

### 4.2 Segurança da conta
Você deve notificar imediatamente a Stonel19, pelo e-mail faglimar@stonel19.com, caso suspeite de uso não autorizado da sua conta. A Stonel19 não se responsabiliza por perdas decorrentes do uso indevido de senhas sob responsabilidade do usuário.

### 4.3 Uma conta por pessoa
Cada usuário deve manter apenas uma conta. É proibido criar múltiplas contas para burlar limites de uso (como o limite diário de mensagens do plano gratuito).

## 5. Uso permitido e proibido

### 5.1 Uso permitido
Você pode usar o TiqChat para fins pessoais, de entretenimento e não comerciais, respeitando estes Termos e a legislação vigente.

### 5.2 Uso proibido
Ao usar o TiqChat, você concorda em **NÃO**:
- Utilizar a Plataforma para fins ilícitos, difamatórios, fraudulentos ou ofensivos;
- Inserir conteúdo que incite ódio, violência, discriminação religiosa, racial, de gênero ou de qualquer natureza;
- Tentar enganar, assediar ou desrespeitar a experiência proposta pela Plataforma;
- Tentar **burlar, contornar ou explorar** limites técnicos (como o limite de mensagens diárias);
- Utilizar robôs, scrapers ou meios automatizados para acessar a Plataforma sem autorização;
- Realizar engenharia reversa, descompilar ou tentar extrair o código-fonte, os prompts ou os modelos de IA;
- Reproduzir, revender ou explorar comercialmente qualquer parte do TiqChat sem autorização expressa;
- Inserir dados pessoais sensíveis de terceiros sem o devido consentimento;
- Usar a Plataforma de forma que prejudique sua segurança, disponibilidade ou integridade.

A violação destas regras poderá resultar em **suspensão ou encerramento imediato** da conta, sem prejuízo das medidas legais cabíveis.

## 6. Planos, pagamentos e assinaturas

### 6.1 Planos disponíveis
O TiqChat oferece:
- **Plano Gratuito:** acesso limitado (atualmente 20 mensagens por dia por personagem), sem necessidade de cartão de crédito.
- **Plano Premium Mensal:** mensagens ilimitadas e recursos adicionais, mediante assinatura recorrente.
- **Plano Premium Anual:** mesmos benefícios do Premium, com pagamento anual e desconto.
- **Cartão Pré-Pago (Gift Card):** acesso por período determinado, sem renovação automática.

Os valores, benefícios e limites de cada plano são informados na página de planos da Plataforma e podem ser **atualizados a qualquer momento**, com aviso prévio aos assinantes ativos.

### 6.2 Processamento de pagamentos
Os pagamentos com cartão de crédito são processados pela **Cielo S.A.**, parceira de meios de pagamento. A Stonel19 **não armazena** os dados completos do seu cartão de crédito — esses dados são tratados diretamente pelo processador de pagamento, conforme padrões de segurança do setor.

### 6.3 Renovação automática
Os planos Premium Mensal e Anual são de **assinatura recorrente**: a cobrança é renovada automaticamente ao final de cada período (mensal ou anual), no valor vigente, até que você cancele. Ao assinar, você autoriza expressamente essas cobranças recorrentes.

### 6.4 Cancelamento
Você pode **cancelar sua assinatura a qualquer momento**, diretamente no seu perfil na Plataforma (Minha Conta → Plano → Cancelar Assinatura) ou pelo WhatsApp (16) 98202-6554. O cancelamento interrompe as cobranças futuras; você continua com acesso Premium até o fim do período já pago, sendo então rebaixado automaticamente para o plano gratuito.

### 6.5 Direito de arrependimento
Conforme o **art. 49 do Código de Defesa do Consumidor**, você tem o direito de se arrepender da contratação no prazo de **7 (sete) dias corridos** a contar da assinatura, com direito à devolução integral dos valores pagos. Para exercer esse direito, entre em contato pelo e-mail faglimar@stonel19.com ou pelo WhatsApp.

### 6.6 Reembolsos
Mesmo após o prazo legal de arrependimento, a Stonel19 adota, como **prática de boa-fé e satisfação do cliente**, a análise de pedidos de reembolso. Caso você esteja insatisfeito com o serviço, entre em contato pelo e-mail faglimar@stonel19.com ou pelo WhatsApp (16) 98202-6554, e avaliaremos a devolução dos valores conforme as circunstâncias.

## 7. Propriedade intelectual

Todo o conteúdo do TiqChat — incluindo nome, marca, logotipo, identidade visual, layout, textos, ilustrações dos personagens, prompts, software e código-fonte — é de **propriedade exclusiva da Stonel19** ou licenciado a ela, e protegido pelas leis de propriedade intelectual.

É **vedada** a reprodução, distribuição, modificação ou uso comercial de qualquer elemento da Plataforma sem autorização prévia e por escrito da Stonel19.

As figuras bíblicas em si pertencem ao domínio público / tradição religiosa; o que é protegido é a **recriação específica, a identidade visual e a implementação técnica** desenvolvidas pela Stonel19.

## 8. Conteúdo das conversas

### 8.1 Suas mensagens
As mensagens que você envia são processadas para gerar as respostas dos personagens. O histórico das suas conversas é armazenado **localmente no seu próprio dispositivo** (navegador), e não em nossos servidores. Para mais detalhes, consulte a [Política de Privacidade](/politica-de-privacidade).

### 8.2 Responsabilidade pelo conteúdo enviado
Você é o único responsável pelo conteúdo que insere nas conversas. Não insira informações confidenciais, dados sensíveis de terceiros ou conteúdo ilícito.

## 9. Limitação de responsabilidade

Na máxima extensão permitida pela legislação aplicável:

- O TiqChat é fornecido **"no estado em que se encontra"** ("as is"), sem garantias de funcionamento ininterrupto ou livre de erros;
- A Stonel19 **não se responsabiliza** por decisões que você tome com base nas respostas geradas pela IA;
- A Stonel19 **não se responsabiliza** por danos indiretos, lucros cessantes ou perdas decorrentes do uso ou da impossibilidade de uso da Plataforma;
- A Stonel19 não garante a disponibilidade contínua de serviços de terceiros dos quais depende (como provedores de IA e de pagamento).

Nada nestes Termos exclui responsabilidades que não possam ser legalmente afastadas, especialmente em relação a direitos do consumidor.

## 10. Suspensão e encerramento de conta

### 10.1 Por iniciativa do usuário
Você pode encerrar sua conta a qualquer momento, entrando em contato pelos canais oficiais.

### 10.2 Por iniciativa da Stonel19
A Stonel19 poderá suspender ou encerrar sua conta, com ou sem aviso prévio, em caso de:
- Violação destes Termos;
- Uso fraudulento, ilícito ou abusivo da Plataforma;
- Determinação legal ou judicial.

## 11. Alterações nos Termos

A Stonel19 poderá atualizar estes Termos periodicamente. Alterações relevantes serão comunicadas pela Plataforma ou por e-mail. A data da "última atualização" no topo deste documento indica a versão vigente. O uso continuado após as alterações representa sua concordância com os novos Termos.

## 12. Disposições gerais

- **Independência das cláusulas:** caso qualquer cláusula seja considerada inválida, as demais permanecem em vigor.
- **Tolerância:** a eventual não exigência de qualquer disposição não representa renúncia ao direito de exigi-la posteriormente.
- **Cessão:** você não pode ceder seus direitos e obrigações sob estes Termos sem autorização da Stonel19.

## 13. Lei aplicável e foro

Estes Termos são regidos pelas leis da **República Federativa do Brasil**.

Fica eleito o **foro da Comarca de Florianópolis, Estado de Santa Catarina**, para dirimir quaisquer controvérsias decorrentes destes Termos, com renúncia a qualquer outro, por mais privilegiado que seja — ressalvado o direito do consumidor de ajuizar ação no foro de seu domicílio, nos termos do Código de Defesa do Consumidor.

## 14. Contato

Em caso de dúvidas sobre estes Termos de Uso, entre em contato:

- **E-mail:** faglimar@stonel19.com
- **WhatsApp:** (16) 98202-6554
- **Endereço:** Rua Profª. Cremilde de Oliveira, 328 — Jardim Atlântico, Florianópolis/SC — CEP 88.095-560
`;

export default function TermsOfUse() {
    return (
        <LegalDocument
            title="Termos de Uso"
            lastUpdate="18 de junho de 2026"
            markdown={CONTENT}
        />
    );
}
